// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { WorkOrder } from '@evolvemep/api-ts';
import { differenceInHours, parseISO } from 'date-fns';

import { isNotNil } from 'helpers/isNotNil';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useUser } from 'hooks/user/useUser';
import {
  type WorkOrderAction,
  useCanEditWorkOrder,
} from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';
import { getWorkOrderRejectedStorageKey } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/WorkOrderRejectedModal';
import { useWorkOrderTrackedModal } from 'modules/Shop/WorkOrders/WorkOrdersPage/modalRegistries';

type Params = {
  workOrder: WorkOrder;
  onWorkOrderActioned: (action: WorkOrderAction) => void;
};

/**
 * Keeps open the prompts a work order's state calls for — its rejection reason, a transfer to confirm, or a
 * cancellation request to decide on — for as long as the calling component is mounted.
 */
export const useWorkOrderStateModals = ({ workOrder, onWorkOrderActioned }: Params) => {
  const { user } = useUser();
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const [lastStatusUpdateAccepted] = useLocalStorage(getWorkOrderRejectedStorageKey(user, workOrder), null);

  const [now] = useState(() => new Date());
  const rejectionAcceptedRecently =
    isNotNil(lastStatusUpdateAccepted) && differenceInHours(now, parseISO(lastStatusUpdateAccepted)) <= 23;

  const canViewCancellationRequest = canEditWorkOrder('denyCancel') || canEditWorkOrder('acceptCancel');
  const canConfirmTransfer = canEditWorkOrder('confirmTransfer');

  useWorkOrderTrackedModal('workOrderRejectedModal', {
    variable: workOrder.workOrderStateName === 'Rejected' && !rejectionAcceptedRecently ? workOrder : null,
    props: (rejectedWorkOrder) => ({ workOrder: rejectedWorkOrder, delayAccept: true }),
  });

  useWorkOrderTrackedModal('workOrderTransferredModal', {
    variable: workOrder.workOrderStateName === 'Transferred' && canConfirmTransfer ? workOrder : null,
    props: (transferredWorkOrder) => ({
      workOrder: transferredWorkOrder,
      onWorkOrderActioned: () => onWorkOrderActioned('confirmTransfer'),
    }),
  });

  useWorkOrderTrackedModal('cancelWorkOrderRequestedModal', {
    variable: workOrder.workOrderStateName === 'Request for Cancel' && canViewCancellationRequest ? workOrder : null,
    props: (requestedWorkOrder) => ({ workOrder: requestedWorkOrder, onWorkOrderActioned }),
  });

  return { canViewCancellationRequest };
};
